import type { ConversationSummary, InboxCounts, InboxFilter } from '@dnx/contracts';
import { cn } from '@dnx/ui';
import { ChannelIcon } from '../components/channel-icon.js';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { AiStatus } from '@dnx/contracts';
import { Bot, Inbox } from 'lucide-react';
import { api } from '../lib/api.js';
import { initials } from '../lib/labels.js';
import { countsQuery, listQuery, relTime, waitTone } from './queries.js';
import { TagChips } from '../reception/tags.js';

const FILTERS: { value: InboxFilter; label: string; count?: keyof InboxCounts }[] = [
  { value: 'waiting', label: 'Aguardando', count: 'waiting' },
  { value: 'mine', label: 'Minhas', count: 'mine' },
  { value: 'open', label: 'Todas', count: 'open' },
  { value: 'closed', label: 'Encerradas' },
];

const WAIT_STYLE = { ok: 'text-fg-muted', warn: 'text-warning font-semibold', late: 'text-danger font-semibold' } as const;

function Item({ c, active, filter, now }: { c: ConversationSummary; active: boolean; filter: InboxFilter; now: number }) {
  const tone = waitTone(c.handledBy === 'queue' ? c.waitingSince : null, now);
  return (
    <Link to="/conversas" search={{ filtro: filter, c: c.id }}
      className={cn('flex gap-3 border-b border-border px-3 py-3 transition-colors hover:bg-surface-muted/70',
        active && 'bg-primary-soft hover:bg-primary-soft')}>
      <span className="relative grid size-10 shrink-0 place-items-center rounded-full bg-surface-muted text-xs font-semibold text-fg-muted ring-1 ring-border">
        {initials(c.contact.name)}
        <span className={cn('absolute -right-1 -bottom-1 grid size-[18px] place-items-center rounded-full bg-surface ring-1 ring-border', c.channel.kind === 'instagram' ? 'text-[#C13584]' : 'text-[#1FA855]')}>
          <ChannelIcon kind={c.channel.kind} className="size-3" />
        </span>
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline justify-between gap-2">
          <span className={cn('truncate text-[13.5px] text-fg', c.unreadCount > 0 ? 'font-semibold' : 'font-medium')}>{c.contact.name}</span>
          <span className={cn('shrink-0 text-[11px] tabular-nums', tone ? WAIT_STYLE[tone] : 'text-fg-subtle')}>
            {relTime(tone ? c.waitingSince! : c.lastMessageAt, now)}
          </span>
        </span>
        <span className="mt-0.5 flex items-center gap-2">
          <span className={cn('min-w-0 flex-1 truncate text-[12.5px]', c.unreadCount > 0 ? 'text-fg' : 'text-fg-muted')}>
            {c.previewDirection === 'out' && <span className="text-fg-subtle">Você: </span>}
            {c.previewDirection === 'auto' && <span className="text-fg-subtle">Automática: </span>}
            {c.previewDirection === 'ai' && <span className="text-fg-subtle">Assistente: </span>}{c.preview ?? 'Sem mensagens'}
          </span>
          {c.unreadCount > 0 && (
            <span className="grid h-5 min-w-5 place-items-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-fg tabular-nums">
              {c.unreadCount}
            </span>
          )}
        </span>
        {c.tags.length > 0 && <TagChips tags={c.tags.slice(0, 3)} className="mt-1.5" />}
        <span className="mt-1 block text-[11px] text-fg-subtle">
          {c.status === 'closed' ? 'Encerrada' : c.handledBy === 'queue' ? 'Na fila' : c.assignedTo ? `Com ${c.assignedTo.name.split(' ')[0]}` : 'Assistente virtual'}
        </span>
      </span>
    </Link>
  );
}

export function ConversationList({ filter, selectedId, now }: { filter: InboxFilter; selectedId?: string; now: number }) {
  const { data: list, isLoading } = useQuery(listQuery(filter));
  const { data: counts } = useQuery(countsQuery);
  const { data: ai } = useQuery({ queryKey: ['ai', 'status'], queryFn: () => api<AiStatus>('/ai/status'), staleTime: 60_000 });
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="border-b border-border px-3 pt-4 pb-2">
        <div className="flex items-center justify-between px-1">
          <h1 className="text-lg font-semibold text-fg">Conversas</h1>
          {ai && (
            <span title={ai.enabled ? `${ai.assistantName} responde dúvidas gerais e agenda, com base em ${ai.approvedDocuments} documentos aprovados` : 'Assistente virtual desligado: todas as conversas vão para a equipe'}
              className={cn('flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium', ai.enabled ? 'bg-primary-soft text-primary' : 'bg-surface-muted text-fg-subtle')}>
              <Bot className="size-3" />{ai.enabled ? 'IA ligada' : 'IA desligada'}
            </span>
          )}
        </div>
        <div className="-mx-1 mt-3 flex gap-0.5 overflow-x-auto px-1 pb-1" role="tablist" aria-label="Filtrar conversas">
          {FILTERS.map((f) => {
            const n = f.count ? counts?.[f.count] : undefined;
            return (
              <Link key={f.value} to="/conversas" search={{ filtro: f.value }} role="tab" aria-selected={filter === f.value}
                className={cn('flex h-8 shrink-0 items-center gap-1 rounded-full px-2 text-[12px] font-medium whitespace-nowrap',
                  filter === f.value ? 'bg-primary text-primary-fg' : 'text-fg-muted hover:bg-surface-muted hover:text-fg')}>
                {f.label}
                {n !== undefined && n > 0 && (
                  <span className={cn('rounded-full px-1.5 text-[11px] tabular-nums',
                    filter === f.value ? 'bg-white/20' : f.value === 'waiting' ? 'bg-warning-soft text-warning' : 'bg-surface-muted')}>{n}</span>
                )}
              </Link>
            );
          })}
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading && Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex gap-3 border-b border-border px-3 py-3">
            <span className="size-10 animate-pulse rounded-full bg-surface-muted" />
            <span className="flex-1 space-y-2 pt-1"><span className="block h-3 w-1/2 animate-pulse rounded bg-surface-muted" /><span className="block h-3 w-4/5 animate-pulse rounded bg-surface-muted" /></span>
          </div>
        ))}
        {list?.map((c) => <Item key={c.id} c={c} active={c.id === selectedId} filter={filter} now={now} />)}
        {list?.length === 0 && (
          <div className="flex flex-col items-center px-6 py-14 text-center">
            <span className="grid size-11 place-items-center rounded-full bg-surface-muted text-fg-subtle"><Inbox className="size-5" /></span>
            <p className="mt-3 text-sm font-medium text-fg">
              {filter === 'waiting' ? 'Ninguém aguardando' : filter === 'mine' ? 'Nenhuma conversa com você' : 'Nenhuma conversa'}
            </p>
            <p className="mt-1 text-[12.5px] text-fg-muted">Quando um paciente mandar mensagem no WhatsApp, ela aparece aqui na hora.</p>
          </div>
        )}
      </div>
    </div>
  );
}
